import { createSlice } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import { showGlobalAlert } from "slices/app/appSlice";

import {
  exportResultsToCsv,
  fetchCurrentPrompts,
  testPrompts,
} from "./actions";

const initialState = {
  prompts: {
    extractionPrompt: "",
    synonymPrompt: "",
  },
  testResults: null,
  loading: false,
  testing: false,
  exporting: false,
  error: null,
  lastTestTimestamp: null,
};

const promptExperimentationSlice = createSlice({
  name: "promptExperimentation",
  initialState,
  reducers: {
    setPrompts: (state, action) => {
      state.prompts = action.payload;
    },
    setExtractionPrompt: (state, action) => {
      state.prompts.extractionPrompt = action.payload;
    },
    setSynonymPrompt: (state, action) => {
      state.prompts.synonymPrompt = action.payload;
    },
    setTestResults: (state, action) => {
      state.testResults = action.payload;
      state.lastTestTimestamp = Date.now();
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setTesting: (state, action) => {
      state.testing = action.payload;
    },
    setExporting: (state, action) => {
      state.exporting = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearTestResults: (state) => {
      state.testResults = null;
      state.lastTestTimestamp = null;
      state.error = null;
    },
    resetToDefaults: (state) => {
      state.prompts = initialState.prompts;
      state.testResults = null;
      state.error = null;
    },
  },
});

export const {
  setPrompts,
  setExtractionPrompt,
  setSynonymPrompt,
  setTestResults,
  setLoading,
  setTesting,
  setExporting,
  setError,
  clearTestResults,
  resetToDefaults,
} = promptExperimentationSlice.actions;

/**
 * Thunk to load current production prompts
 */
export const loadCurrentPrompts = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const prompts = await fetchCurrentPrompts();
    if (prompts) {
      dispatch(setPrompts(prompts));
    }
  } catch (error) {
    console.error("Failed to load current prompts:", error);
    dispatch(setError("Failed to load current prompts"));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.error,
        text: "Failed to load current prompts. Please try again.",
      })
    );
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Thunk to test custom prompts
 */
export const runPromptTest = (params) => async (dispatch) => {
  dispatch(setTesting(true));
  dispatch(setError(null));

  try {
    const results = await testPrompts(params);
    if (results) {
      dispatch(setTestResults(results));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `Test completed! Extracted ${results.metrics.totalTermsExtracted} terms using ${results.metrics.totalTokens ?? "n/a"} tokens.`,
        })
      );
    }
    return results;
  } catch (error) {
    console.error("Failed to test prompts:", error);
    const errorMessage =
      error.response?.data?.message || "Failed to test prompts";
    dispatch(setError(errorMessage));
    dispatch(
      showGlobalAlert({
        variant: alertVariants.error,
        text: errorMessage,
      })
    );
    return null;
  } finally {
    dispatch(setTesting(false));
  }
};

/**
 * Thunk to export test results to CSV
 */
export const exportToCsv = (results) => async (dispatch) => {
  dispatch(setExporting(true));

  try {
    const blob = await exportResultsToCsv(results);

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Generate filename with timestamp
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    link.download = `Prompt_Test_Results_${results.opportunityId}_${timestamp}.csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Results exported successfully!",
      })
    );
  } catch (error) {
    console.error("Failed to export results:", error);
    dispatch(
      showGlobalAlert({
        variant: alertVariants.error,
        text: "Failed to export results. Please try again.",
      })
    );
  } finally {
    dispatch(setExporting(false));
  }
};

export default promptExperimentationSlice.reducer;
